import { useFormikContext } from 'formik'
import { useRef } from 'react'
import AutoCompleteField, { VField, VFieldFile } from '../../components/Fields'
import { API_URL } from '../../constants'
import { useProvinceList } from '../../hooks/province'
import { useToponimiList } from '../../hooks/toponimi'
import { Cliente, Sede } from '../../types'
import { useIsAmministratore, useIsSupervisore } from '../../utils/utils'

type FormSedeProps = {
  cliente: Cliente | undefined
  showDetailSede: boolean
  setShowDetailSede: (value: boolean) => void
}

export default function FormSede({ cliente, showDetailSede, setShowDetailSede }: FormSedeProps) {
  const { values, setFieldValue } = useFormikContext<Sede>()
  const province = useProvinceList()
  const toponimi = useToponimiList()
  const dvrInputRef = useRef<HTMLInputElement>(null)
  const isAmministratore = useIsAmministratore()
  const isSupervisore = useIsSupervisore()

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <div className="heading-h3">{cliente?.ragione_sociale}</div>
          <div className="d-flex align-items-center">
            <div className="p-md">
              <span className="text-body-secondary">Numero rea:</span>
              <span className="ms-2">{cliente?.sede_principale?.numero_rea}</span>
            </div>
            <div className="p-md ms-4">
              <span className="text-body-secondary">Partita iva:</span>
              <span className="ms-2">{cliente?.sede_principale?.partita_iva}</span>
            </div>
            <div className="p-md ms-4">
              <span className="text-body-secondary">Numero sedi:</span>
              <span className="ms-2">{cliente?.numero_sedi}</span>
            </div>
          </div>
        </div>
      </div>
      {showDetailSede ? (
        <div className="row mt-3 align-items-end">
          <div className="col-5">
            <div className="form-group mb-3">
              <label className="label-m label-field">Numero REA</label>
              <div>{values.numero_rea}</div>
            </div>
          </div>
          <div className="col-5">
            <div className="form-group mb-3">
              <label className="label-m label-field">Partita IVA</label>
              <div>{values.partita_iva}</div>
            </div>
          </div>
          <div className="col-2">
            <div className="form-group mb-3">
              <label className="label-m label-field">Unità locale</label>
              <div>{values.unita_locale}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="form-group mb-3">
              <label className="label-m label-field">Toponimo</label>
              <div>{values.toponimo_data?.nome}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="form-group mb-3">
              <label className="label-m label-field">Indirizzo</label>
              <div>{values.via}</div>
            </div>
          </div>
          <div className="col-2">
            <div className="form-group mb-3">
              <label className="label-m label-field">Civico</label>
              <div>{values.civico}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group mb-3">
              <label className="label-m label-field">Comune</label>
              <div>{values.comune_data?.nome}</div>
            </div>
          </div>
          <div className="col-2">
            <div className="form-group mb-3">
              <label className="label-m label-field">Provincia</label>
              <div>{values.provincia_data?.sigla}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="form-group mb-3">
              <label className="label-m label-field">Nazione</label>
              <div>{values.nazione}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="form-group mb-3">
              <label className="label-m label-field">Telefono</label>
              <div>{values.telefono}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group mb-3">
              <label className="label-m label-field">Email referente</label>
              <div>{values.email_referente}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group mb-3">
              <label className="label-m label-field">Nome referente</label>
              <div>{values.nome_referente}</div>
            </div>
          </div>
          <div className="col-6">
            <VFieldFile name="dvr" label="DVR" ref={dvrInputRef} readOnly />
          </div>
        </div>
      ) : (
        <div className="row mt-3 align-items-end">
          <div className="col-5">
            <VField name="numero_rea" label="Numero REA" required />
          </div>
          <div className="col-5">
            <VField name="partita_iva" label="Partita IVA" />
          </div>
          <div className="col-2">
            <VField name="unita_locale" label="Unità locale" />
          </div>
          <div className="col-3">
            <VField name="toponimo" label="Toponimo" as="select" required>
              <option value="">Seleziona un toponimo</option>
              {toponimi?.data?.map((toponimo) => (
                <option key={toponimo.id} value={toponimo.id}>
                  {toponimo.nome}
                </option>
              ))}
            </VField>
          </div>
          <div className="col-6">
            <VField name="via" label="Indirizzo" required />
          </div>
          <div className="col-2">
            <VField name="civico" label="Civico" />
          </div>
          <div className="col-6">
            <AutoCompleteField
              name={`comune`}
              label="Comune"
              required
              onChange={(value) => {
                setFieldValue(`comune`, value.value)
                setFieldValue(`provincia`, value?.provincia_pk)
              }}
              url={`${API_URL}/bilance/comune/options`}
              value={values.comune}
            />
          </div>
          <div className="col-4">
            <VField name="provincia" label="Provincia" as="select" required>
              <option value="">Seleziona una provincia</option>
              {province?.data?.map((provincia) => (
                <option key={provincia.id} value={provincia.id}>
                  {provincia.sigla}
                </option>
              ))}
            </VField>
          </div>
          <div className="col-4">
            <VField name="nazione" label="Nazione" required />
          </div>
          <div className="col-4">
            <VField name="telefono" label="Telefono" />
          </div>
          <div className="col-6">
            <VField name="email_referente" label="Email referente" />
          </div>
          <div className="col-6">
            <VField name="nome_referente" label="Nome referente" />
          </div>
          {(isAmministratore || isSupervisore) && (
            <div className="col-6">
              <VFieldFile name="dvr" label="DVR" ref={dvrInputRef} accept=".pdf" />
            </div>
          )}
        </div>
      )}
    </>
  )
}
